import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Grid } from "@mui/material";
import {
  Refresh as RefreshIcon,
  Queue as QueueIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import { useDataProvider, useNotify } from "react-admin";

interface QueueStats {
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  total: number;
}

interface QueueJob {
  id: string;
  data: any;
  createdAt: number;
  delay?: number;
  priority?: number;
  processedOn?: number;
  finishedOn?: number;
  returnvalue?: any;
  failedReason?: string;
  stacktrace?: string;
  attemptsMade?: number;
}

interface QueueDetails {
  waiting: QueueJob[];
  active: QueueJob[];
  completed: QueueJob[];
  failed: QueueJob[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`queue-tabpanel-${index}`}
      aria-labelledby={`queue-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const NotificationQueueMonitor = () => {
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [queueDetails, setQueueDetails] = useState<QueueDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const loadQueueData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log("Загружаем данные очереди...");
      const [stats, details] = await Promise.all([
        (dataProvider as any).getQueueStats(),
        (dataProvider as any).getQueueDetails(20),
      ]);

      console.log("Получены данные очереди:", { stats, details });

      // Проверяем, что данные не пустые
      if (!stats || typeof stats !== "object") {
        throw new Error("Получены некорректные данные статистики очереди");
      }

      if (!details || typeof details !== "object") {
        throw new Error("Получены некорректные данные деталей очереди");
      }

      setQueueStats(stats);
      setQueueDetails(details);
      console.log("Данные очереди успешно установлены");
    } catch (err) {
      console.error("Ошибка при загрузке данных очереди:", err);
      setError("Ошибка при загрузке данных очереди");
      notify("Ошибка при загрузке данных очереди", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQueueData();

    // Автообновление каждые 30 секунд
    const interval = setInterval(loadQueueData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString("ru-RU");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "waiting":
        return "default";
      case "active":
        return "primary";
      case "completed":
        return "success";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "waiting":
        return <ScheduleIcon />;
      case "active":
        return <PlayArrowIcon />;
      case "completed":
        return <CheckCircleIcon />;
      case "failed":
        return <ErrorIcon />;
      default:
        return <QueueIcon />;
    }
  };

  if (loading && !queueStats && !error) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={loadQueueData}>
            Повторить
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Мониторинг очереди уведомлений
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadQueueData}
          disabled={loading}
        >
          Обновить
        </Button>
      </Box>

      {queueStats && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 3 }}>
          <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <ScheduleIcon color="action" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" gutterBottom>
                    Ожидают
                  </Typography>
                </Box>
                <Typography variant="h4">{queueStats.waiting}</Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <PlayArrowIcon color="primary" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" gutterBottom>
                    Активные
                  </Typography>
                </Box>
                <Typography variant="h4">{queueStats.active}</Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" gutterBottom>
                    Завершены
                  </Typography>
                </Box>
                <Typography variant="h4">{queueStats.completed}</Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <ErrorIcon color="error" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" gutterBottom>
                    Ошибки
                  </Typography>
                </Box>
                <Typography variant="h4">{queueStats.failed}</Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {queueDetails && (
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab
                label={`Ожидают (${queueDetails.waiting.length})`}
                icon={<ScheduleIcon />}
                iconPosition="start"
              />
              <Tab
                label={`Активные (${queueDetails.active.length})`}
                icon={<PlayArrowIcon />}
                iconPosition="start"
              />
              <Tab
                label={`Завершены (${queueDetails.completed.length})`}
                icon={<CheckCircleIcon />}
                iconPosition="start"
              />
              <Tab
                label={`Ошибки (${queueDetails.failed.length})`}
                icon={<ErrorIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Уведомление</TableCell>
                    <TableCell>Приоритет</TableCell>
                    <TableCell>Задержка</TableCell>
                    <TableCell>Создано</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queueDetails.waiting.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>{job.id}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {job.data?.payload?.title || "Без заголовка"}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {job.data?.payload?.type} →{" "}
                            {job.data?.payload?.channel}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.priority || 1}
                          size="small"
                          color="default"
                        />
                      </TableCell>
                      <TableCell>
                        {job.delay ? `${Math.round(job.delay / 1000)}с` : "Нет"}
                      </TableCell>
                      <TableCell>{formatTimestamp(job.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Уведомление</TableCell>
                    <TableCell>Приоритет</TableCell>
                    <TableCell>Начато</TableCell>
                    <TableCell>Длительность</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queueDetails.active.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>{job.id}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {job.data?.payload?.title || "Без заголовка"}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {job.data?.payload?.type} →{" "}
                            {job.data?.payload?.channel}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.priority || 1}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        {formatTimestamp(job.processedOn || 0)}
                      </TableCell>
                      <TableCell>
                        {job.processedOn
                          ? `${Math.round((Date.now() - job.processedOn) / 1000)}с`
                          : "Неизвестно"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Уведомление</TableCell>
                    <TableCell>Завершено</TableCell>
                    <TableCell>Результат</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queueDetails.completed.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>{job.id}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {job.data?.payload?.title || "Без заголовка"}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {job.data?.payload?.type} →{" "}
                            {job.data?.payload?.channel}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {formatTimestamp(job.finishedOn || 0)}
                      </TableCell>
                      <TableCell>
                        <Chip label="Успешно" size="small" color="success" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Уведомление</TableCell>
                    <TableCell>Попытки</TableCell>
                    <TableCell>Ошибка</TableCell>
                    <TableCell>Детали</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queueDetails.failed.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>{job.id}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {job.data?.payload?.title || "Без заголовка"}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {job.data?.payload?.type} →{" "}
                            {job.data?.payload?.channel}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.attemptsMade || 0}
                          size="small"
                          color="error"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="error">
                          {job.failedReason || "Неизвестная ошибка"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {job.stacktrace && (
                          <Accordion>
                            <AccordionSummary>
                              <Typography variant="caption">
                                Стек ошибки
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <pre
                                style={{
                                  fontSize: "10px",
                                  background: "#f5f5f5",
                                  padding: "8px",
                                  borderRadius: "4px",
                                  overflow: "auto",
                                  maxHeight: "200px",
                                }}
                              >
                                {job.stacktrace}
                              </pre>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Card>
      )}
    </Box>
  );
};
